import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { createAppointment } from '../../../services/appointmentService';
import { trackAppointmentStarted, trackAppointmentBooked } from '../../../services/userJourneyService';
import { gaTrackAppointmentBooked } from '../../../services/gaAnalyticsService';
import { sendAppointmentConfirmationEmail } from '../../../services/resendEmailService';
import { useAuth } from '../../../contexts/AuthContext';

const SchedulingSection = ({ property }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    scheduledDate: '',
    appointmentType: 'Property Visit',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const appointmentTypeOptions = [
    { value: 'Property Visit', label: 'Visite de propriété' },
    { value: 'Investment Consultation', label: 'Consultation investissement' },
    { value: 'Documentation Review', label: 'Révision des documents' },
    { value: 'Financial Discussion', label: 'Discussion financière' },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!formData?.scheduledDate) {
      setError('Veuillez sélectionner une date et heure');
      return;
    }
    trackAppointmentStarted(property);
    try {
      setIsSubmitting(true);
      setError(null);
      const appointmentData = {
        property_id: property?.id,
        agent_id: property?.agent_id,
        scheduled_date: new Date(formData?.scheduledDate)?.toISOString(),
        appointment_type: formData?.appointmentType,
        notes: formData?.notes || `Visite programmée pour ${property?.title}`,
        location: property?.address,
        duration_minutes: 60,
        status: 'scheduled'
      };
      const result = await createAppointment(appointmentData);
      if (result?.error) { setError(result?.error); return; }
      trackAppointmentBooked(property, { type: formData?.appointmentType, scheduledDate: formData?.scheduledDate });
      gaTrackAppointmentBooked(property, { type: formData?.appointmentType, scheduledDate: formData?.scheduledDate });
      if (user?.email) {
        sendAppointmentConfirmationEmail({
          user_id: user?.id,
          client_name: user?.user_metadata?.full_name || user?.email,
          client_email: user?.email,
          appointment_type: formData?.appointmentType,
          scheduled_date: new Date(formData?.scheduledDate)?.toISOString(),
          scheduled_time: new Date(formData?.scheduledDate)?.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
          duration_minutes: 60,
          location: property?.address,
        });
      }
      setSubmitted(true);
      setFormData({ scheduledDate: '', appointmentType: 'Property Visit', notes: '' });
    } catch (err) {
      console.error('Error creating appointment:', err);
      setError('Une erreur est survenue lors de la programmation. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow?.setDate(tomorrow?.getDate() + 1);
    return tomorrow?.toISOString()?.slice(0, 16);
  };

  const getMaxDate = () => {
    const maxDate = new Date();
    maxDate?.setMonth(maxDate?.getMonth() + 3);
    return maxDate?.toISOString()?.slice(0, 16);
  };

  if (!property) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-muted rounded w-1/2"></div>
          <div className="h-10 bg-muted rounded"></div>
          <div className="h-10 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="text-center py-4">
          <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Calendar" size={28} color="#16a34a" />
          </div>
          <h3 className="font-heading font-semibold text-base text-foreground mb-2">
            Rendez-vous programmé !
          </h3>
          <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
            Votre demande de visite a été enregistrée. Notre agent vous contactera pour confirmer les détails.
          </p>
          <button
            onClick={() => setSubmitted(false)}
            className="text-sm font-medium text-brand-gold hover:text-brand-earth transition-colors"
          >
            Programmer une autre visite
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border p-5">
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <Icon name="Calendar" size={18} color="var(--color-brand-gold)" />
        <h3 className="font-heading font-semibold text-base text-foreground">
          Programmer une visite
        </h3>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-xs flex items-start gap-2">
            <Icon name="AlertCircle" size={14} color="currentColor" className="flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {/* Appointment Type */}
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">
            Type de rendez-vous
          </label>
          <Select
            options={appointmentTypeOptions}
            value={formData?.appointmentType}
            onChange={(val) => setFormData(prev => ({ ...prev, appointmentType: val }))}
            placeholder="Choisir un type"
          />
        </div>

        {/* Date & Time */}
        <div className="space-y-1.5">
          <label htmlFor="scheduledDate" className="text-sm font-medium text-foreground">
            Date et heure souhaitées <span className="text-destructive">*</span>
          </label>
          <Input
            id="scheduledDate"
            name="scheduledDate"
            type="datetime-local"
            value={formData?.scheduledDate}
            onChange={handleInputChange}
            min={getMinDate()}
            max={getMaxDate()}
            required
          />
          <p className="text-xs text-muted-foreground">
            Disponible du lundi au samedi, de 8h à 18h
          </p>
        </div>

        {/* Notes */}
        <div className="space-y-1.5">
          <label htmlFor="notes" className="text-sm font-medium text-foreground">
            Notes complémentaires
          </label>
          <textarea
            id="notes"
            name="notes"
            value={formData?.notes}
            onChange={handleInputChange}
            placeholder="Questions spécifiques, préférences de visite..."
            rows={2}
            className="w-full px-3 py-2 text-sm bg-background border border-input rounded-md resize-none placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 transition-colors"
          />
        </div>

        <Button
          type="submit"
          variant="elegant"
          size="lg"
          fullWidth
          disabled={isSubmitting}
          iconName={isSubmitting ? 'Loader' : 'Calendar'}
          iconPosition="left"
        >
          {isSubmitting ? 'Programmation en cours...' : 'Programmer la visite'}
        </Button>
      </form>
      {/* Quick Actions */}
      <div className="mt-5 pt-4 border-t border-border">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Actions rapides</p>
        <div className="space-y-2">
          {[
            { icon: 'Video', label: 'Visite virtuelle', action: 'ExternalLink' },
            { icon: 'FileText', label: 'Dossier complet', action: 'Download' },
            { icon: 'Calculator', label: 'Simulation crédit', action: 'ExternalLink' },
          ]?.map(({ icon, label, action }) => (
            <button
              key={label}
              className="w-full flex items-center justify-between p-2.5 bg-muted/40 hover:bg-muted rounded-md transition-colors text-left"
            >
              <div className="flex items-center gap-2.5">
                <Icon name={icon} size={15} color="var(--color-brand-gold)" />
                <span className="text-sm text-foreground">{label}</span>
              </div>
              <Icon name={action} size={13} color="var(--color-muted-foreground)" />
            </button>
          ))}
        </div>
      </div>
      {/* Contact Info */}
      <div className="mt-4 p-3 bg-muted/40 rounded-md border border-border">
        <div className="flex items-center gap-2 mb-1.5">
          <Icon name="Info" size={14} color="var(--color-brand-gold)" />
          <span className="text-xs font-semibold text-foreground">Besoin d'aide ?</span>
        </div>
        <p className="text-xs text-muted-foreground mb-2">
          Notre équipe est disponible pour vous accompagner
        </p>
        <div className="flex flex-col gap-1 text-xs text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <Icon name="Phone" size={11} color="currentColor" />
            <span>+225 01 41 07 02 71</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Icon name="Mail" size={11} color="currentColor" />
            <span>contact@kapitalstates.com</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SchedulingSection;